/**
 * 概览页「运营看板」统一面板 —— 全平台运营数据 + 按仿真类型实时状态，融为一块。
 *
 * 原本概览页顶部是两块独立区块：DashboardSection（累计/今日/趋势）与 SimTypeSection
 * （PNC / 感知 的排队/编译/执行/已完成状态卡）。二者共享同一套 Linear 亮色与
 * 「任务=PNC 靛蓝 / 里程=感知 翠绿」的配色语义，故合并成一块面板，自上而下：
 *
 *   ① 平台指标带：累计任务 / 累计里程（hero）+ 今日新增任务 / 里程（accent 增量）。
 *   ② 运行趋势折线（日 / 周 / 月 / 历史累计 切换）。
 *   ③ 按仿真类型子卡：PNC / 感知，各含实时 4 状态格 + 进入任务列表入口。
 *
 * 数据源（三份，随 refreshToken 一起刷新）：
 *   - GET /sim-dashboard/summary、GET /sim-dashboard/trend  —— 公开，读 job_stat 汇总。
 *   - GET /overview/simtype-runtime                          —— 实时状态。
 * 里程后端存米，前端 ÷1000 转 km。
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Segmented } from 'antd';
import { AimOutlined, EyeOutlined, RightOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import {
  getDashboardSummary,
  getDashboardTrend,
  type DashboardSummaryGroupVo,
  type DashboardSummaryVo,
  type DashboardTrendPoint,
  type TrendGranularity,
} from '@/apis/simDashboard';
import { getSimTypeRuntime, type SimTypeRuntimeVo } from '@/apis/overview';
import './DashboardSection.less';

/** 双指标 / 双仿真类型主色。 */
const COLOR_TASK = '#5e6ad2'; // 任务数 = PNC 靛蓝
const COLOR_MILEAGE = '#10b981'; // 里程 = 感知 翠绿

/** 米 → 公里，保留 1 位小数。 */
const toKm = (meters: number): number => Math.round((meters / 1000) * 10) / 10;

/** 千分位整数。 */
const fmtInt = (n: number): string => n.toLocaleString('en-US');

const GRANULARITY_OPTIONS: { label: string; value: TrendGranularity }[] = [
  { label: '按日', value: 'day' },
  { label: '按周', value: 'week' },
  { label: '按月', value: 'month' },
  { label: '历史累计', value: 'all' },
];

/** 按仿真类型子卡静态描述（运行时数据另拉）。 */
interface SimTypeSpec {
  key: string; // 后端 sim_type：planning / perception
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  accent: string;
  statusTag: string;
  ctaLabel: string;
  onEnter: () => void;
}

interface Props {
  /** 父级递增的刷新令牌；每变一次重拉 summary + trend + runtime。 */
  refreshToken: number;
  /** 按仿真类型子卡描述（PNC / 感知）。 */
  simTypes: SimTypeSpec[];
}

export default function DashboardSection({ refreshToken, simTypes }: Props) {
  const [summary, setSummary] = useState<DashboardSummaryVo | null>(null);
  const [granularity, setGranularity] = useState<TrendGranularity>('day');
  const [trend, setTrend] = useState<DashboardTrendPoint[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [runtime, setRuntime] = useState<SimTypeRuntimeVo[] | null>(null);

  // KPI summary
  useEffect(() => {
    let alive = true;
    getDashboardSummary()
      .then((res) => {
        if (alive) setSummary(res.summary);
      })
      .catch(() => {
        // 静默：概览页非关键区块，失败不打断
      });
    return () => {
      alive = false;
    };
  }, [refreshToken]);

  // 按仿真类型实时状态
  useEffect(() => {
    let alive = true;
    getSimTypeRuntime()
      .then((res) => {
        if (alive) setRuntime(res.items);
      })
      .catch(() => {
        // 静默
      });
    return () => {
      alive = false;
    };
  }, [refreshToken]);

  // 趋势（随粒度 / 刷新变化重拉）
  useEffect(() => {
    let alive = true;
    setTrendLoading(true);
    // day/week/month 给足回溯桶数；all 为按天历史累计曲线，后端返回全量不受 periods 限制。
    const periods = granularity === 'day' ? 30 : granularity === 'week' ? 26 : 24;
    getDashboardTrend(granularity, periods)
      .then((res) => {
        if (alive) setTrend(res.points);
      })
      .catch(() => {
        // 静默
      })
      .finally(() => {
        if (alive) setTrendLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [granularity, refreshToken]);

  const runtimeByKey = useMemo(() => {
    const m = new Map<string, SimTypeRuntimeVo>();
    runtime?.forEach((r) => m.set(r.sim_type, r));
    return m;
  }, [runtime]);

  /** summary 按 group_key（planning / perception）索引，供累计卡取分组数。 */
  const summaryByKey = useMemo(() => {
    const m = new Map<string, DashboardSummaryGroupVo>();
    summary?.groups?.forEach((g) => m.set(g.group_key, g));
    return m;
  }, [summary]);

  const chartOption: EChartsOption = useMemo(() => {
    const x = trend.map((p) => p.bucket);
    const taskData = trend.map((p) => p.task_num);
    const mileageData = trend.map((p) => toKm(p.mileage));
    const showSymbol = trend.length <= 40;
    return {
      color: [COLOR_TASK, COLOR_MILEAGE],
      tooltip: { trigger: 'axis', axisPointer: { type: 'line' } },
      legend: { data: ['任务数', '里程 (km)'], right: 0, top: 0, icon: 'roundRect' },
      grid: { left: 8, right: 8, top: 40, bottom: 24, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: x,
        axisTick: { alignWithLabel: true },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#8c8c8c' },
      },
      yAxis: [
        {
          type: 'value',
          name: '任务数',
          minInterval: 1,
          nameTextStyle: { color: '#8c8c8c', fontSize: 11 },
          axisLabel: { color: '#8c8c8c' },
          splitLine: { lineStyle: { color: '#f0f0f2' } },
        },
        {
          type: 'value',
          name: 'km',
          nameTextStyle: { color: '#8c8c8c', fontSize: 11 },
          axisLabel: { color: '#8c8c8c' },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: '任务数',
          type: 'line',
          yAxisIndex: 0,
          smooth: true,
          showSymbol,
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: { width: 2 },
          areaStyle: { color: COLOR_TASK, opacity: 0.08 },
          data: taskData,
        },
        {
          name: '里程 (km)',
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          showSymbol,
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: { width: 2 },
          data: mileageData,
        },
      ],
    };
  }, [trend]);

  return (
    <section className="ops-panel">
      <div className="ops-panel__head">
        <p className="ops-panel__eyebrow">Operations</p>
        <h2 className="ops-panel__title">概览</h2>
        <p className="ops-panel__subtitle">PNC + 感知 · 累计产出 / 实时状态 / 运行趋势</p>
      </div>

      {/* ① 平台指标带：PNC / 感知 各一张累计卡（今日增量作卡底小字） */}
      <div className="ops-panel__totals">
        {simTypes.map((spec) => (
          <TotalCard key={spec.key} spec={spec} group={summaryByKey.get(spec.key)} />
        ))}
      </div>

      {/* ② 按仿真类型实时状态 */}
      <div className="ops-panel__section-label">
        <span className="ops-panel__section-tick" />
        <span className="ops-panel__section-text">按仿真类型 · 实时状态</span>
        <span className="ops-panel__section-rule" />
      </div>
      <div className="ops-panel__simtypes">
        {simTypes.map((spec) => (
          <SimTypeCard key={spec.key} spec={spec} runtime={runtimeByKey.get(spec.key)} />
        ))}
      </div>

      {/* ③ 运行趋势折线 */}
      <div className="ops-panel__trend">
        <div className="ops-panel__section-label">
          <span className="ops-panel__section-tick" />
          <span className="ops-panel__section-text">运行趋势</span>
          <span className="ops-panel__section-rule" />
          <Segmented
            size="small"
            options={GRANULARITY_OPTIONS}
            value={granularity}
            onChange={(v) => setGranularity(v as TrendGranularity)}
          />
        </div>
        <ReactECharts
          className="ops-panel__chart"
          option={chartOption}
          notMerge
          lazyUpdate
          showLoading={trendLoading}
          style={{ height: 300, width: '100%' }}
        />
      </div>

      {/* ④ 页脚：进入 PNC / 感知任务列表入口（固定在最底部） */}
      <div className="ops-panel__footer">
        {simTypes.map((spec) => (
          <button
            key={spec.key}
            type="button"
            className="ops-panel__cta"
            onClick={spec.onEnter}
            style={
              {
                '--op-cta-accent': spec.accent,
                '--op-cta-wash': `${spec.accent}0d`,
                '--op-cta-shadow': `${spec.accent}40`,
              } as React.CSSProperties
            }
          >
            {spec.ctaLabel}
            <RightOutlined className="ops-panel__cta-arrow" />
          </button>
        ))}
      </div>
    </section>
  );
}

interface TotalCardProps {
  spec: SimTypeSpec;
  group?: DashboardSummaryGroupVo;
}

/** 单个仿真类型（PNC / 感知）的累计卡：任务数 + 里程双 hero，今日增量作卡底小字。 */
function TotalCard({ spec, group }: TotalCardProps) {
  const accent = spec.accent;
  const has = group != null;
  return (
    <div className="ops-panel__total" style={{ ['--op-total-accent' as string]: accent }}>
      <p className="ops-panel__total-head">
        <span className="ops-panel__total-dot" style={{ background: accent }} />
        {spec.title}
        <span className="ops-panel__total-scope">累计</span>
      </p>

      <div className="ops-panel__total-metrics">
        <div className="ops-panel__total-metric">
          <div className="ops-panel__total-value">
            {has ? fmtInt(group!.total_task_num) : '—'}
          </div>
          <div className="ops-panel__total-cap">任务数</div>
        </div>
        <div className="ops-panel__total-metric">
          <div className="ops-panel__total-value">
            {has ? fmtInt(toKm(group!.total_mileage)) : '—'}
            <span className="ops-panel__total-unit">km</span>
          </div>
          <div className="ops-panel__total-cap">里程</div>
        </div>
      </div>

      <div className="ops-panel__total-today">
        <span className="ops-panel__total-today-badge">今日</span>
        <span className="ops-panel__total-today-item">
          任务 <b>+{has ? fmtInt(group!.today_task_num) : 0}</b>
        </span>
        <span className="ops-panel__total-today-sep" />
        <span className="ops-panel__total-today-item">
          里程 <b>+{has ? fmtInt(toKm(group!.today_mileage)) : 0}</b> km
        </span>
      </div>
    </div>
  );
}

interface SimTypeCardProps {
  spec: SimTypeSpec;
  runtime?: SimTypeRuntimeVo;
}

function SimTypeCard({ spec, runtime }: SimTypeCardProps) {
  const accent = spec.accent;
  return (
    <div
      className="ops-panel__simcard"
      style={{ background: `linear-gradient(135deg, ${accent}12 0%, #ffffff 55%)` }}
    >
      <div className="ops-panel__simcard-head">
        <div
          className="ops-panel__simcard-icon"
          style={{ background: `${accent}1a`, color: accent }}
        >
          {spec.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ops-panel__simcard-title">{spec.title}</div>
          <div className="ops-panel__simcard-sub">{spec.subtitle}</div>
        </div>
        <span
          className="ops-panel__simcard-tag"
          style={{ background: `${accent}1e`, color: accent }}
        >
          {spec.statusTag}
        </span>
      </div>

      <div className="ops-panel__tiles">
        <RuntimeTile label="排队中" value={runtime?.queued} accent="#d97706" />
        <RuntimeTile label="编译中" value={runtime?.compiling} accent="#8b5cf6" />
        <RuntimeTile label="执行中" value={runtime?.running} accent={accent} />
        <RuntimeTile
          label="已完成"
          rangeLabel="今日"
          value={runtime?.done_today}
          accent="#16a34a"
          sub={
            runtime?.pass_rate_today != null
              ? `通过率 ${(runtime.pass_rate_today * 100).toFixed(1)}%`
              : undefined
          }
        />
      </div>
    </div>
  );
}

interface TileProps {
  label: string;
  value: number | undefined;
  accent: string;
  rangeLabel?: string;
  sub?: string;
}

function RuntimeTile({ label, value, accent, rangeLabel, sub }: TileProps) {
  return (
    <div
      className="ops-panel__tile"
      style={{ ['--op-tile-accent' as string]: accent }}
    >
      <div className="ops-panel__tile-label">
        <span style={{ width: 8, height: 8, borderRadius: 2, background: accent }} />
        {label}
        {rangeLabel && <span className="ops-panel__tile-badge">{rangeLabel}</span>}
      </div>
      <div className="ops-panel__tile-value" style={{ color: accent }}>
        {value ?? '—'}
      </div>
      {sub && <div className="ops-panel__tile-sub">{sub}</div>}
    </div>
  );
}

/** 默认 PNC / 感知 两个仿真类型描述（导航由外部注入）。 */
export const DEFAULT_SIM_TYPES = (
  navigate: (to: string) => void,
): SimTypeSpec[] => [
  {
    key: 'planning',
    title: 'PNC 仿真',
    subtitle: 'planning · 回归 / 场景批量 / 评测器矩阵',
    icon: <AimOutlined />,
    accent: COLOR_TASK,
    statusTag: '正常',
    ctaLabel: '进入 PNC 任务列表',
    onEnter: () => navigate('/task-list?sim_type=planning'),
  },
  {
    key: 'perception',
    title: '感知仿真',
    subtitle: 'perception · mAP / 漏检 / 误检',
    icon: <EyeOutlined />,
    accent: COLOR_MILEAGE,
    statusTag: '预览',
    ctaLabel: '进入感知任务列表',
    onEnter: () => navigate('/task-list?sim_type=perception'),
  },
];
